// ABOUTME: Screen 3A Who Felt It: reach tiles over the stack; confirm_reaches sends the selected reach codes, nothing_reached sends none.
// ABOUTME: The tiles start from the stored reaches; confirm_reaches is drawn only once a tile is selected, nothing_reached always.

import { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useCrossingContent } from '../../../hooks/useCrossingContent';
import type { ScreenProps, ScreenResult } from '../runner/screenContract';
import { visuallyHidden } from '../runner/visuallyHidden';
import { NO_STORM, NO_STORM_3A_HEADLINE, NO_STORM_3A_SUB } from './clouds/noStorm';
import ImpactSkyMask from './impact/ImpactSkyMask';
import ReachTiles from './impact/ReachTiles';

const sameCodes = (a: string[], b: string[]) => a.length === b.length && a.every(code => b.includes(code));

export default function Screen3A({ crossing, runner }: ScreenProps): ScreenResult {
  const { data: content } = useCrossingContent();
  const reachTypes = useMemo(() => Object.values(content?.reachTypes ?? {}), [content]);
  const storedAll = useMemo(() => (crossing.reaches ?? []).map(reach => reach.reachCode), [crossing.reaches]);
  const [selected, setSelected] = useState<string[]>(storedAll);

  const toggle = (code: string) => setSelected(current => (
    current.includes(code) ? current.filter(c => c !== code) : [...current, code]
  ));

  // Sent in tile order. Until the reach types load there is no order to follow, so the selection goes as it is.
  const tileCodes = reachTypes.map(reach => reach.code);
  const loaded = tileCodes.length > 0;
  const reaches = loaded ? tileCodes.filter(code => selected.includes(code)) : selected;
  // A stored reach with no tile (a retired type) is not something the child can change, so it is not "unsent".
  const stored = loaded ? storedAll.filter(code => tileCodes.includes(code)) : storedAll;
  const isNoStorm = crossing.storm === NO_STORM;

  return {
    layer: (
      <>
        <ImpactSkyMask />
        <Box role="status" sx={visuallyHidden}>{reaches.length ? `${reaches.length} selected` : 'None selected'}</Box>
      </>
    ),
    // The tiles are centred over the stack, so the cloud stack draws them.
    cloudTiers: { tiles: <ReachTiles reachTypes={reachTypes} selected={selected} onToggle={toggle} /> },
    headline: isNoStorm ? NO_STORM_3A_HEADLINE : 'A Storm at the wheel can reach past us and touch what matters.',
    sub: isNoStorm ? NO_STORM_3A_SUB : 'Tap what it reached this time — one or more. Tap again to remove.',
    slots: {
      // Both are held until the tiles exist: with no reach types loaded, "Nothing reached" would skip the repair phase unseen.
      confirm_reaches: { values: { reaches }, disabled: !loaded, hidden: reaches.length === 0 },
      // nothing_reached fades in after 2.1 s, as the prototype's does; confirm_reaches appears with a pick and does not.
      nothing_reached: {
        disabled: !loaded,
        fadesIn: true,
        onPress: () => {
          setSelected([]);
          runner.run('nothing_reached', { reaches: [] });
        },
      },
    },
    hasUnsentState: !sameCodes(reaches, stored),
  };
}
